import { Component, type ErrorInfo, type ReactNode } from 'react'
import { translate } from '../i18n'

interface Props {
  children: ReactNode
  /** Nom de la zone, affiche dans le message (ex: "Détection pubs"). */
  label?: string
}

interface State {
  error: Error | null
}

/**
 * Empeche qu'une exception de rendu (code pays inconnu, statut inattendu...)
 * blanchisse toute l'application. Isole la panne a la zone concernee.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info.componentStack)
  }

  private reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="error-state" role="alert">
        <span className="error-state-icon" aria-hidden="true">⚠</span>
        <div className="error-state-body">
          {/* Composant de classe : pas de hook, donc `translate` et non `useT`.
              Un changement de langue est suivi d'un remontage de l'ecran. */}
          <p className="error-state-title">
            {this.props.label
              ? translate('error.inZone', { label: this.props.label })
              : translate('error.generic')}
          </p>
          <p className="error-state-msg">{error.message}</p>
        </div>
        <button type="button" className="btn btn-secondary error-state-retry" onClick={this.reset}>
          {translate('common.retry')}
        </button>
      </div>
    )
  }
}
